import { BlaestProduct } from "@/formatters/types";
import {
  formatBreadcrumbs,
  formatCustomAttributes,
  formatMeasurementChart,
  formatPrices,
  formatProductSizes,
  getCanonicalCategory,
  getSpecs,
} from "@/formatters/utils";
import type { BaseSelectionItem } from "@frend-digital/centra-types/selection";
import { baseFormat, defineFormatter } from "@frend-digital/centra/formatting";

export const rootFormatter = defineFormatter(
  (product: BlaestProduct) => {
    const breadcrumbs = formatBreadcrumbs(product);
    const customAttributes = formatCustomAttributes(product);
    const formattedResult = {
      ...baseFormat(product),
      name: product.name!,
      breadcrumbs,
      variantName: product.variantName!,
      prices: formatPrices(product),
      uri: product.uri!,
      videoUrl: product.dis_video_video_url,
      itemId: product.product,
      shortDescription: product.excerptHtml,
      sku: product.sku,
      sizes: formatProductSizes(product),
      sizeGuide: formatMeasurementChart(product),
      core: {
        name: product.name,
        slug: product.uri,
        variantName: product.variantName,
      },
      seo: {
        metaTitle: product.metaTitle,
        metaDescription: product.metaDescription,
        metaKeywords: product.metaKeywords,
      },
      relation: product.relation,
      soldOut: product.items?.every(
        (item) => item.stock === "no" || (item.stock as unknown) === false,
      ),
      description: product.description,
      specs: getSpecs(product),
      centraProduct: product.centraProduct,
      centraVariant: product.centraVariant,
      ...customAttributes,
    };

    return formattedResult;
  },
  {
    displayAsVariant: true,
  },
);

export const formatProductDetails = (product: BlaestProduct) => {
  const formattedProduct = rootFormatter(product);

  const genders = product.relatedProducts
    ?.map((relatedProduct: BlaestProduct) => {
      if (
        relatedProduct.relation === "gender" &&
        relatedProduct.product !== product.product
      ) {
        return rootFormatter(relatedProduct);
      }
    })
    .filter(Boolean);

  const relatedMap = new Map<
    string,
    NonNullable<(typeof product)["relatedProducts"]>[number] & {
      relatedProducts: (typeof product)["relatedProducts"];
    }
  >();

  for (const relation of product.relatedProducts || []) {
    if (!("uri" in relation) || relation.relation !== "standard") continue;
    const item = relatedMap.get(relation.uri!);
    if (item) {
      item.relatedProducts ??= [];
      relation.relation = "variant";
      item.relatedProducts.push(relation);
    } else {
      relatedMap.set(relation.uri!, {
        ...relation,
        relatedProducts: [],
      });
    }
  }

  const relatedProducts = Array.from(relatedMap.values()).map(rootFormatter);

  return {
    ...formattedProduct,
    genders,
    relatedProducts,
  };
};

export type ProductDetails = NonNullable<
  ReturnType<typeof formatProductDetails>
>;

export const formatProductCard = (product: BlaestProduct) => {
  const formattedProduct = rootFormatter(product);

  const sizes = formattedProduct.sizes;

  const soldOut = sizes?.every((size) => size.stock === false);

  return {
    ...formattedProduct,
    breadcrumbs: formattedProduct.breadcrumbs,
    id: product.product,
    labels: formattedProduct.labels,
    relatedProducts: null,
    sku: product.sku,
    soldOut,
    showVideo: product?.dis_video_video_plp === "1",
    description: {
      name: product.name,
      price: formattedProduct.prices,
      sku: product.sku,
      cannonicalCategory: getCanonicalCategory(product),
      variants: formattedProduct.variants?.map((variant) => ({
        variantName: variant.variantName,
        itemId: variant.itemId,
        uri: variant.uri,
        sku: variant.sku,
      })),
      swatch: formattedProduct.swatch,
      sizes: formatProductSizes(product),
    },
    depictListingResultId: undefined as string | undefined,
    depictSearchResultId: undefined as string | undefined,
    depictProductId: product?.items?.at(0)?.item?.split("-")?.[1],
  };
};

export const formatCartItems = (items?: BaseSelectionItem[]) => {
  return (
    items?.map((item) => ({
      item: item.item,
      name: item?.product?.name,
      series: item?.product?.pr_series,
      shape: item?.product?.pr_shape,
      uri: item?.product?.uri,
      variant: item?.product?.variantName,
      size: item?.size,
      id: item?.line,
      price: item?.priceEach,
      quantity: item?.quantity,
      media: {
        standard: item.product?.media?.["standard"].map((url) => ({
          href: url,
          alt: `${item?.product?.name}-${item?.product?.variantName}`,
        })),
        full: item.product?.media?.["full"].map((url) => ({
          href: url,
          alt: `${item?.product?.name}-${item?.product?.variantName}`,
        })),
      },
      priceAsNumber: item?.priceEachAsNumber,
      discountedPrice: item?.priceEachBeforeDiscount,
      itemId: item?.item,
      line: item.line ?? "",
      sku: item?.sku,
      category: item?.product?.category,
      productId: item?.product?.product,
      productSku: item?.product?.sku,
    })) ?? []
  );
};

export type FormattedCartItem = ReturnType<typeof formatCartItems>[number];

export type ProductCard = NonNullable<ReturnType<typeof formatProductCard>>;

export type FormattedCartItems = ReturnType<typeof formatCartItems>;

export const formatSeoProduct = defineFormatter((product: BlaestProduct) => {
  return {
    displayItemId: product.product!,
    uri: product.uri!,
    name: product.name!,
    variantName: product.variantName!,
    gender: product?.dis_gender ?? "",
    canonicalCategoryId: product.category!,
    canonicalCategoryUri: product.categoryUri,
    canonicalUri: product.categoryUri
      ? `${product.categoryUri}/${product.uri}`
      : undefined,
    lastModified: product.modifiedAt,
  };
});

export type SeoProduct = ReturnType<typeof formatSeoProduct>;
